/**
 * BI Cleanup Validation Page
 * Human review interface for transaction classification
 * 
 * Features:
 * - Transaction list with confidence indicators
 * - One-click approve/reclassify
 * - Bulk actions
 * - Progress tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useUser, useAuth } from '../contexts/TestAuthContext';
import axios from 'axios';
import { useSwipeable } from 'react-swipeable';
import AppLayout from '../components/AppLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Types
interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  payee: string | null;
  classification: {
    category: string;
    subcategory: string | null;
    confidence: number;
    confidence_level: 'high' | 'medium' | 'low';
    model_used: string;
  };
  needs_review: boolean;
  validation_status: 'pending' | 'approved' | 'rejected' | 'modified';
  source: {
    filename: string;
    page_number: number;
  };
  learned?: boolean;  // Indicates if this category was learned from user correction
  learned_from?: string;  // Pattern it was learned from
}

interface JobStats {
  total_transactions: number;
  auto_approved: number;
  needs_review: number;
  reviewed: number;
  high_confidence: number;
  medium_confidence: number;
  low_confidence: number;
  anomaly_count?: number;
  critical_anomalies?: number;
  high_anomalies?: number;
}

interface JobStatus {
  job_id: string;
  status: string;
  stats: JobStats;
  progress_pct: number;
  error: string | null;
  report_id?: string;
  report_name?: string;
  business_name?: string;
  bank_name?: string;
  statement_period?: string;
}

// Category options
const CATEGORIES = [
  "Rent & Occupancy",
  "Utilities",
  "Insurance",
  "Payroll & Benefits",
  "Professional Services",
  "Marketing & Advertising",
  "Technology & Software",
  "Office & Supplies",
  "Travel & Entertainment",
  "Vehicle & Transportation",
  "Inventory & Merchandise",
  "Food & Beverage Cost",
  "Manufacturing",
  "Sales Revenue",
  "Other Income",
  "Bank & Financial Fees",
  "Taxes & Licenses",
  "Debt Service",
  "Miscellaneous",
  "Transfer",
  // Exclude from claim (not a business expense)
  "------- EXCLUDE FROM CLAIM -------",
  "Owner Draw"
];

// Confidence badge component
function ConfidenceBadge({ level, confidence }: { level: string; confidence: number }) {
  // Use confidence percentage to determine color (more intuitive than level)
  const getColorByPercentage = () => {
    const pct = confidence * 100;
    if (pct >= 90) return 'bg-green-100 text-green-800 border-green-300';
    if (pct >= 70) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getColorByPercentage()}`}>
      {(confidence * 100).toFixed(0)}%
    </span>
  );
}

// Transaction row component
function TransactionRow({ 
  transaction, 
  selected,
  highlighted,
  onSelect,
  onApprove, 
  onReclassify 
}: { 
  transaction: Transaction;
  selected: boolean;
  highlighted?: boolean;
  onSelect: (id: string) => void;
  onApprove: (id: string) => void;
  onReclassify: (id: string, category: string) => void;
}) {
  const [showCategories, setShowCategories] = useState(false);
  
  const formatAmount = (amount: number) => {
    const formatted = Math.abs(amount).toLocaleString('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    });
    return amount < 0 ? `-${formatted}` : formatted;
  };
  
  const getStatusBadge = () => {
    // Check validation_status first, then fall back to auto_approved
    switch (transaction.validation_status) {
      case 'approved':
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">✓ Approved</span>;
      case 'modified':
        return <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">✎ Modified</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">✗ Rejected</span>;
      default:
        // If auto_approved but no validation_status, show as auto-approved
        if (transaction.auto_approved) {
          return <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">✓ Auto-Approved</span>;
        }
        return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">⏳ Needs Review</span>;
    }
  };
  
  return (
    <tr 
      id={`txn-${transaction.id}`}
      className={`border-b hover:bg-gray-50 transition-colors ${
        highlighted ? 'bg-yellow-200 ring-2 ring-yellow-500 ring-offset-2 shadow-lg border-l-4 border-l-yellow-500 animate-pulse' : 
        selected ? 'bg-blue-50' : ''
      }`}
    >
      <td className="py-3 px-4">
        <input 
          type="checkbox" 
          checked={selected}
          onChange={() => onSelect(transaction.id)}
          className="rounded border-gray-300"
        />
      </td>
      <td className="py-3 px-4 text-sm text-gray-600">{transaction.date}</td>
      <td className="py-3 px-4">
        <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={transaction.description}>
          {transaction.description}
        </div>
        {transaction.payee && (
          <div className="text-xs text-gray-500">{transaction.payee}</div>
        )}
      </td>
      <td className={`py-3 px-4 text-sm font-mono ${transaction.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
        {formatAmount(transaction.amount)}
      </td>
      <td className="py-3 px-4">
        <div className="relative">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowCategories(!showCategories)}
              className="text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer"
            >
              {transaction.classification.category}
            </button>
            {/* Learned Badge - shows when category came from AI learning */}
            {transaction.learned && (
              <span 
                className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full font-medium"
                title="Category learned from your previous corrections"
              >
                📚 Learned
              </span>
            )}
          </div>
          {showCategories && (
            <div className="absolute z-10 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => {
                    onReclassify(transaction.id, cat);
                    setShowCategories(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer ${
                    cat === transaction.classification.category ? 'bg-blue-50 font-medium' : ''
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        <ConfidenceBadge 
          level={transaction.classification.confidence_level} 
          confidence={transaction.classification.confidence}
        />
      </td>
      <td className="py-3 px-4">{getStatusBadge()}</td>
      <td className="py-3 px-4">
        {(transaction.validation_status === 'pending' || transaction.validation_status === null || !transaction.validation_status) && (
          <div className="flex gap-2">
            <button
              onClick={() => onApprove(transaction.id)}
              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 cursor-pointer"
            >
              Approve
            </button>
            <button
              onClick={() => setShowCategories(!showCategories)}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer"
            >
              Edit
            </button>
          </div>
        )}
        {transaction.validation_status === 'approved' && (
          <span className="text-green-600 text-sm">✓ Approved</span>
        )}
        {transaction.validation_status === 'modified' && (
          <span className="text-blue-600 text-sm">✓ Modified</span>
        )}
      </td>
    </tr>
  );
}

// Mobile Transaction Card with Swipe Gestures
function TransactionCard({ 
  transaction, 
  selected,
  highlighted,
  onSelect,
  onApprove, 
  onReclassify 
}: { 
  transaction: Transaction;
  selected: boolean;
  highlighted?: boolean;
  onSelect: (id: string) => void;
  onApprove: (id: string) => void;
  onReclassify: (id: string, category: string) => void;
}) {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [showCategories, setShowCategories] = useState(false);
  const [swiping, setSwiping] = useState(false);
  
  const formatAmount = (amount: number) => {
    const formatted = Math.abs(amount).toLocaleString('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    });
    return amount < 0 ? `-${formatted}` : formatted;
  };
  
  const swipeHandlers = useSwipeable({
    onSwiping: (e) => {
      if (transaction.validation_status === 'approved' || transaction.validation_status === 'modified') return;
      setSwiping(true);
      setSwipeOffset(Math.max(-100, Math.min(100, e.deltaX)));
    },
    onSwipedLeft: () => {
      if (transaction.validation_status === 'approved' || transaction.validation_status === 'modified') return;
      if (swipeOffset < -60) {
        setShowCategories(true);
      }
      setSwipeOffset(0);
      setSwiping(false);
    },
    onSwipedRight: () => {
      if (transaction.validation_status === 'approved' || transaction.validation_status === 'modified') return;
      if (swipeOffset > 60) {
        onApprove(transaction.id);
      }
      setSwipeOffset(0);
      setSwiping(false);
    },
    onSwiped: () => {
      setSwipeOffset(0);
      setSwiping(false);
    },
    delta: 10,
    trackMouse: false,
    preventScrollOnSwipe: true
  });
  
  const getConfidenceColor = () => {
    // Use confidence percentage to determine color (more intuitive)
    const pct = transaction.classification.confidence * 100;
    if (pct >= 90) return 'bg-green-100 text-green-800 border-green-300';
    if (pct >= 70) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };
  
  const getStatusBadge = () => {
    switch (transaction.validation_status) {
      case 'approved':
        return <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">✓ Approved</span>;
      case 'modified':
        return <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">✎ Modified</span>;
      case 'rejected':
        return <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">✗ Rejected</span>;
      default:
        // If auto_approved but no validation_status, show as auto-approved
        if (transaction.auto_approved) {
          return <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">✓ Auto</span>;
        }
        return <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">⏳ Review</span>;
    }
  };
  
  // Consider auto-approved transactions as not pending
  const isPending = !transaction.auto_approved && (!transaction.validation_status || transaction.validation_status === 'pending');
  
  return (
    <div className="relative overflow-hidden">
      {/* Swipe Background Indicators */}
      {isPending && (
        <>
          <div className={`absolute inset-y-0 left-0 w-24 bg-green-500 flex items-center justify-center transition-opacity ${swipeOffset > 30 ? 'opacity-100' : 'opacity-0'}`}>
            <span className="text-white font-semibold">✓ Approve</span>
          </div>
          <div className={`absolute inset-y-0 right-0 w-24 bg-blue-500 flex items-center justify-center transition-opacity ${swipeOffset < -30 ? 'opacity-100' : 'opacity-0'}`}>
            <span className="text-white font-semibold">Edit ✎</span>
          </div>
        </>
      )}
      
      {/* Card Content */}
      <div 
        id={`txn-${transaction.id}`}
        {...swipeHandlers}
        className={`bg-white p-4 border-b border-gray-200 transition-transform ${swiping ? '' : 'transition-all duration-200'} ${
          highlighted ? 'bg-yellow-200 ring-2 ring-yellow-500 ring-offset-2 shadow-lg border-l-4 border-l-yellow-500 animate-pulse' : 
          selected ? 'bg-blue-50' : ''
        }`}
        style={{ transform: `translateX(${swipeOffset}px)` }}
      >
        {/* Header Row: Date, Amount, Status */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <input 
              type="checkbox" 
              checked={selected}
              onChange={() => onSelect(transaction.id)}
              className="rounded border-gray-300 w-5 h-5"
            />
            <span className="text-sm text-gray-500">{transaction.date}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-base font-semibold ${transaction.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
              {formatAmount(transaction.amount)}
            </span>
            {getStatusBadge()}
          </div>
        </div>
        
        {/* Description */}
        <div className="mb-3">
          <p className="text-sm font-medium text-gray-900 line-clamp-2">{transaction.description}</p>
          {transaction.payee && (
            <p className="text-xs text-gray-500 mt-0.5">{transaction.payee}</p>
          )}
        </div>
        
        {/* Category & Confidence */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowCategories(!showCategories)}
              className="text-sm text-blue-600 font-medium hover:text-blue-800 flex items-center gap-1"
            >
              <span>📁</span> {transaction.classification.category}
              <svg className={`w-4 h-4 transition-transform ${showCategories ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {/* Learned Badge for mobile */}
            {transaction.learned && (
              <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full font-medium">
                📚
              </span>
            )}
          </div>
          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getConfidenceColor()}`}>
            {(transaction.classification.confidence * 100).toFixed(0)}% confidence
          </span>
        </div>
        
        {/* Category Selector (Expandable) */}
        {showCategories && (
          <div className="mb-3 grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg max-h-48 overflow-y-auto">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => {
                  onReclassify(transaction.id, cat);
                  setShowCategories(false);
                }}
                className={`p-2 text-xs text-left rounded-lg transition-colors ${
                  cat === transaction.classification.category 
                    ? 'bg-blue-100 text-blue-800 font-medium border-2 border-blue-300' 
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
        
        {/* Action Buttons - Only for pending transactions */}
        {isPending && (
          <div className="flex gap-2">
            <button
              onClick={() => onApprove(transaction.id)}
              className="flex-1 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors flex items-center justify-center gap-2"
            >
              <span>✓</span> Approve
            </button>
            <button
              onClick={() => setShowCategories(!showCategories)}
              className="flex-1 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center justify-center gap-2"
            >
              <span>✎</span> Edit Category
            </button>
          </div>
        )}
        
        {/* Swipe Hint - Only for pending */}
        {isPending && (
          <p className="text-xs text-gray-400 text-center mt-2 md:hidden">
            ← Swipe left to edit • Swipe right to approve →
          </p>
        )}
      </div>
    </div>
  );
}

// Main component
export default function CleanupValidation() {
  const { jobId: jobIdFromPath } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();
  
  // Resolved job ID - either from path or resolved from draft query param
  const [resolvedJobId, setResolvedJobId] = useState<string | null>(jobIdFromPath || null);
  const [resolving, setResolving] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<'all' | 'needs_review' | 'approved'>('all'); // Default to 'all' to show everything
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [anomalies, setAnomalies] = useState<any[]>([]);
  const [showAnomalies, setShowAnomalies] = useState(true); // Default to showing anomalies
  const [showAllAnomalies, setShowAllAnomalies] = useState(false); // For expand/collapse more anomalies
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [highlightedTransactionId, setHighlightedTransactionId] = useState<string | null>(null); // For anomaly linking
  const [wizardContext, setWizardContext] = useState<{
    from_wizard: boolean;
    return_to: string;
    step: number;
    report_id?: string;
  } | null>(null);
  
  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [amountMin, setAmountMin] = useState<string>('');
  const [amountMax, setAmountMax] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Resolve job ID from draft query parameter if not provided in path
  useEffect(() => {
    const resolveJobFromDraft = async () => {
      // If we already have a job ID from path, use it
      if (jobIdFromPath) {
        setResolvedJobId(jobIdFromPath);
        return;
      }
      
      // Check for draft query parameter
      const draftId = searchParams.get('draft');
      if (!draftId) {
        setError('No job ID or draft ID provided');
        setLoading(false);
        return;
      }
      
      // Resolve job ID from draft/report ID
      setResolving(true);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${BACKEND_URL}/api/v1/cleanup/jobs/by-report/${draftId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const resolvedId = response.data.job_id;
        console.log('🔵 Resolved cleanup job ID from draft:', draftId, '->', resolvedId);
        setResolvedJobId(resolvedId);
        
        // Optionally redirect to the canonical URL with jobId in path
        // navigate(`/cleanup/validation/${resolvedId}`, { replace: true });
      } catch (err: any) {
        console.error('Failed to resolve job from draft:', err);
        setError(err.response?.data?.detail || `No cleanup job found for this case. The case may not have any bank statements uploaded yet.`);
        setLoading(false);
      } finally {
        setResolving(false);
      }
    };
    
    resolveJobFromDraft();
  }, [jobIdFromPath, searchParams, getToken]);
  
  // Use resolved job ID throughout the component
  const jobId = resolvedJobId;
  
  // Check for wizard context on mount
  useEffect(() => {
    const contextStr = sessionStorage.getItem('wizard_context');
    if (contextStr) {
      try {
        const context = JSON.parse(contextStr);
        if (context.from_wizard) {
          setWizardContext(context);
        }
      } catch (e) {
        console.error('Failed to parse wizard context:', e);
      }
    }
  }, []);
  
  // Filter to only show unresolved anomalies
  const unresolvedAnomalies = anomalies.filter(a => !a.is_resolved);
  
  const getAuthHeader = useCallback(async () => {
    const token = await getToken();
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);
  
  // Load job status
  const loadJobStatus = useCallback(async () => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}`,
        { headers }
      );
      setJobStatus(response.data);
    } catch (err: any) {
      console.error('Failed to load job status:', err);
    }
  }, [jobId, getAuthHeader]);
  
  // Load anomalies
  const loadAnomalies = useCallback(async () => {
    try {
      const headers = await getAuthHeader();
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/cleanup/anomalies/${jobId}`,
        { headers }
      );
      setAnomalies(response.data);
    } catch (err: any) {
      console.error('Failed to load anomalies:', err);
    }
  }, [jobId, getAuthHeader]);
  
  // Load transactions
  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeader();
      
      const params: any = { page, page_size: 50 };
      if (filter === 'needs_review') {
        params.needs_review = true;
      }
      
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/transactions`,
        { headers, params }
      );
      
      setTransactions(response.data.transactions);
      setTotalPages(response.data.pagination.total_pages);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [jobId, page, filter, getAuthHeader]);
  
  useEffect(() => {
    // Don't fetch until we have a resolved job ID
    if (!jobId) {
      return;
    }
    
    const fetchData = async () => {
      console.log('🔵 fetchData called, jobId:', jobId);
      try {
        const token = await getToken();
        console.log('🔵 Got token:', token);
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch job status
        console.log('🔵 Fetching job status...');
        const statusRes = await axios.get(
          `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}`,
          { headers }
        );
        console.log('🔵 Got job status:', statusRes.data);
        setJobStatus(statusRes.data);
        
        // Fetch transactions
        setLoading(true);
        const txnRes = await axios.get(
          `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/transactions`,
          { headers, params: { page: 1, page_size: 50 } }
        );
        setTransactions(txnRes.data.transactions);
        setTotalPages(txnRes.data.pagination.total_pages);
        setLoading(false);
        
        // Fetch anomalies
        const anomRes = await axios.get(
          `${BACKEND_URL}/api/v1/cleanup/anomalies/${jobId}`,
          { headers }
        );
        setAnomalies(anomRes.data);
      } catch (err: any) {
        console.error('🔴 Failed to fetch data:', err);
        setError(err.response?.data?.detail || 'Failed to load data');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [jobId, getToken]); // Only re-run when jobId changes
  
  // Auto-refresh when job is processing - faster polling during extraction
  useEffect(() => {
    const isProcessing = jobStatus?.status && ['pending', 'extracting', 'classifying'].includes(jobStatus.status);
    
    if (isProcessing) {
      // Poll faster during extraction phase for better UX
      const pollInterval = jobStatus?.status === 'extracting' ? 2000 : 5000;
      const interval = setInterval(() => {
        loadJobStatus();
      }, pollInterval);
      
      return () => clearInterval(interval);
    }
  }, [jobStatus?.status, loadJobStatus]);
  
  // Approve transaction
  const handleApprove = async (transactionId: string) => {
    try {
      const headers = await getAuthHeader();
      await axios.post(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/review`,
        { transaction_id: transactionId, action: 'approve' },
        { headers }
      );
      
      // Update local state
      setTransactions(prev => prev.map(t => 
        t.id === transactionId 
          ? { ...t, validation_status: 'approved' as const, needs_review: false }
          : t
      ));
      loadJobStatus();
      loadAnomalies(); // Reload anomalies after approval
    } catch (err: any) {
      console.error('Failed to approve:', err);
    }
  };
  
  // Reclassify transaction
  const handleReclassify = async (transactionId: string, newCategory: string) => {
    try {
      const headers = await getAuthHeader();
      
      // Find the transaction to get original category
      const transaction = transactions.find(t => t.id === transactionId);
      const originalCategory = transaction?.classification?.category || 'Unknown';
      
      // Update the transaction
      await axios.post(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/review`,
        { transaction_id: transactionId, action: 'modify', new_category: newCategory },
        { headers }
      );
      
      // Record the learning for AI feedback loop
      if (user?.id && transaction && originalCategory !== newCategory) {
        try {
          await axios.post(
            `${BACKEND_URL}/api/v1/cleanup/learnings/record`,
            {
              user_id: user.id,
              description: transaction.description,
              payee: transaction.payee || null,
              original_category: originalCategory,
              corrected_category: newCategory,
              amount: transaction.amount,
              transaction_id: transactionId,
              job_id: jobId
            },
            { headers }
          );
          console.log('📚 AI Learning recorded:', transaction.description, '->', newCategory);
        } catch (learnErr) {
          // Don't fail the main operation if learning fails
          console.warn('Failed to record learning:', learnErr);
        }
      }
      
      setTransactions(prev => prev.map(t => 
        t.id === transactionId 
          ? { 
              ...t, 
              classification: { ...t.classification, category: newCategory, confidence: 1.0 },
              validation_status: 'modified' as const, 
              needs_review: false,
              learned: true  // Mark as learned from user correction
            }
          : t
      ));
      loadJobStatus();
      loadAnomalies(); // Reload anomalies after reclassification
    } catch (err: any) {
      console.error('Failed to reclassify:', err);
    }
  };
  
  // Bulk approve
  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      const headers = await getAuthHeader();
      await axios.post(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/bulk-review`,
        { transaction_ids: Array.from(selectedIds), action: 'approve' },
        { headers }
      );
      
      setTransactions(prev => prev.map(t => 
        selectedIds.has(t.id)
          ? { ...t, validation_status: 'approved' as const, needs_review: false }
          : t
      ));
      setSelectedIds(new Set());
      loadJobStatus();
      loadAnomalies(); // Reload anomalies after bulk approval
    } catch (err: any) {
      console.error('Failed to bulk approve:', err);
    }
  };
  
  // Toggle selection
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  
  // Select all - now loads ALL transactions if needed
  const toggleSelectAll = async () => {
    if (selectedIds.size === transactions.length && transactions.length > 0) {
      // Deselect all
      setSelectedIds(new Set());
    } else {
      // If we have all transactions loaded, select them
      // Otherwise fetch all transaction IDs first
      if (jobStatus && transactions.length < jobStatus.stats.total) {
        try {
          const headers = await getAuthHeader();
          // Load all transactions (no pagination) to get all IDs
          const response = await axios.get(
            `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/transactions`,
            { headers, params: { page_size: 1000 } } // Get all
          );
          const allTransactions = response.data.transactions;
          setTransactions(allTransactions);
          setSelectedIds(new Set(allTransactions.map((t: any) => t.id)));
        } catch (err) {
          console.error('Failed to load all transactions:', err);
          // Fall back to selecting visible only
          setSelectedIds(new Set(transactions.map(t => t.id)));
        }
      } else {
        setSelectedIds(new Set(transactions.map(t => t.id)));
      }
    }
  };
  
  // Export CSV
  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const headers = await getAuthHeader();
      
      // Use fetch API to avoid XMLHttpRequest responseType conflicts
      const response = await fetch(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/export?format=csv`,
        { 
          method: 'GET',
          headers: {
            ...headers,
            'Accept': 'text/csv'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cleanup_${jobId?.slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Show completion modal if job is complete
      if (jobStatus?.stats.needs_review === 0) {
        setShowCompletionModal(true);
      }
    } catch (err: any) {
      console.error('Failed to export:', err);
      setError('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Mark job as complete
  const handleMarkComplete = async () => {
    // If there are still items needing review, confirm with user
    if (jobStatus && jobStatus.stats.needs_review > 0) {
      const confirmed = window.confirm(
        `You still have ${jobStatus.stats.needs_review} transaction(s) flagged for review.\n\n` +
        `Do you want to complete anyway?\n\n` +
        `• Approved items will be included in your report\n` +
        `• Unreviewed items will be auto-approved as-is`
      );
      if (!confirmed) return;
    }
    
    try {
      const headers = await getAuthHeader();
      await axios.post(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/complete`,
        {},
        { headers }
      );
      loadJobStatus();
      setShowCompletionModal(true);
    } catch (err: any) {
      console.error('Failed to mark complete:', err);
      setError('Failed to mark as complete. Please try again.');
    }
  };

  // Create report from cleanup data
  const handleCreateReport = async () => {
    try {
      const headers = await getAuthHeader();
      
      // Get wizard-ready financial data from cleanup
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/wizard-data`,
        { headers }
      );
      
      const wizardData = response.data;
      console.log('📊 Wizard data from cleanup:', wizardData);
      
      // Store in sessionStorage for wizard to pick up
      sessionStorage.setItem('cleanup_wizard_data', JSON.stringify({
        type: 'cleanup',
        job_id: jobId,
        financial_data: wizardData.financial_data,
        reconstructed_pl: wizardData.reconstructed_pl,  // P&L reconstruction data
        summary: wizardData.summary,
        metadata: wizardData.metadata
      }));
      
      // Clear wizard context since we're completing the flow
      sessionStorage.removeItem('wizard_context');
      
      // Navigate to wizard (or resume existing report)
      if (wizardContext?.report_id) {
        navigate(`/wizard-v2?resume=${wizardContext.report_id}`);
      } else {
        navigate('/wizard-v2');
      }
    } catch (err: any) {
      console.error('Failed to prepare report data:', err);
      setError('Failed to prepare report data. Please try again.');
    }
  };

  // Return to wizard without creating report (cancel cleanup flow)
  const handleReturnToWizard = () => {
    sessionStorage.removeItem('wizard_context');
    if (wizardContext?.report_id) {
      navigate(`/wizard-v2?resume=${wizardContext.report_id}`);
    } else {
      navigate('/wizard-v2');
    }
  };

  // Save job progress for later continuation
  const [saving, setSaving] = useState(false);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [businessNameInput, setBusinessNameInput] = useState('');
  
  const handleSaveForLater = async (overrideName?: string) => {
    // If no business name exists, prompt for one
    const currentBusinessName = overrideName || jobStatus?.business_name;
    
    if (!currentBusinessName && !showNamePrompt) {
      // Suggest a name from the filename if available
      const suggestedName = jobStatus?.stats ? 
        `Bank Statement Upload - ${new Date().toLocaleDateString()}` : '';
      setBusinessNameInput(suggestedName);
      setShowNamePrompt(true);
      return;
    }
    
    try {
      setSaving(true);
      const headers = await getAuthHeader();
      
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/save-for-later`,
        { business_name: currentBusinessName || businessNameInput || 'Unnamed Job' },
        { headers }
      );
      
      console.log('💾 Job saved for later:', response.data);
      
      // Show success feedback briefly then navigate
      setShowCompletionModal(false);
      setShowNamePrompt(false);
      
      // Navigate to dashboard - job will appear there for resumption
      navigate('/dashboard');
      
    } catch (err) {
      console.error('Failed to save job:', err);
      setError('Failed to save progress. Please try again.');
      setSaving(false);
    }
  };

  // Show processing state when job is still being processed
  // Check this BEFORE the loading state to show file progress during extraction
  const isProcessing = jobStatus?.status && ['pending', 'extracting', 'classifying'].includes(jobStatus.status);
  
  // DEBUG: Log the current state
  console.log('CleanupValidation render:', { jobStatus, isProcessing, loading, resolving, resolvedJobId, error });
  
  // Show resolving state when looking up job from draft ID
  if (resolving) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Finding cleanup job for this case...</p>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  // Show error state if no job found
  if (error && !jobId) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md p-8">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">📋</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">No Transaction Data Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/queue')}
                className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                ← Back to Work Queue
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  // Show dedicated processing UI when job is still being processed
  if (isProcessing) {
    const totalFiles = (jobStatus as any)?.total_files || 0;
    const processedFiles = (jobStatus as any)?.processed_files || 0;
    const progressPct = jobStatus?.progress_pct || 0;
    
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-lg p-8">
            <div className="relative mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl">📄</span>
              </div>
            </div>
            
            <h2 className="text-xl font-bold text-gray-900 mb-2">Processing Your Documents</h2>
            
            {/* File count display */}
            {totalFiles > 0 && (
              <div className="mb-4 bg-blue-100 rounded-lg px-4 py-3">
                <p className="text-lg font-semibold text-blue-800">
                  📁 {processedFiles} / {totalFiles} files processed
                </p>
                {/* Progress bar */}
                <div className="mt-2 h-2 bg-blue-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 rounded-full transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <p className="text-sm text-blue-700 mt-1">{progressPct}% complete</p>
              </div>
            )}
            
            <p className="text-gray-600 mb-6">
              {jobStatus?.status === 'extracting' && 'Extracting transactions from your bank statements...'}
              {jobStatus?.status === 'classifying' && 'Classifying and categorizing transactions...'}
              {jobStatus?.status === 'pending' && 'Preparing to process your files...'}
            </p>
            
            <div className="bg-blue-50 rounded-lg p-4 text-left mb-6">
              <h3 className="font-medium text-blue-900 mb-2">What's happening:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li className={`flex items-center gap-2 ${jobStatus?.status === 'pending' ? 'font-semibold' : 'text-blue-600'}`}>
                  {jobStatus?.status === 'pending' ? '🔄' : '✅'} Uploading files
                </li>
                <li className={`flex items-center gap-2 ${jobStatus?.status === 'extracting' ? 'font-semibold' : jobStatus?.status === 'pending' ? 'text-blue-400' : 'text-blue-600'}`}>
                  {jobStatus?.status === 'extracting' ? '🔄' : jobStatus?.status === 'pending' ? '⏳' : '✅'} Extracting transactions (OCR)
                  {jobStatus?.status === 'extracting' && totalFiles > 0 && (
                    <span className="ml-1 text-xs text-blue-600">({processedFiles}/{totalFiles})</span>
                  )}
                </li>
                <li className={`flex items-center gap-2 ${jobStatus?.status === 'classifying' ? 'font-semibold' : ['pending', 'extracting'].includes(jobStatus?.status || '') ? 'text-blue-400' : 'text-blue-600'}`}>
                  {jobStatus?.status === 'classifying' ? '🔄' : ['pending', 'extracting'].includes(jobStatus?.status || '') ? '⏳' : '✅'} AI Classification
                </li>
                <li className="flex items-center gap-2 text-blue-400">
                  ⏳ Ready for review
                </li>
              </ul>
            </div>
            
            <p className="text-sm text-gray-500">
              This may take a few minutes for large uploads. You can leave this page and return later - 
              your progress will be saved automatically.
            </p>
            
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-4 py-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              ← Return to Dashboard
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  // Fallback loading state when not processing but still loading data
  if (loading && !transactions.length && !isProcessing) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading transactions...</p>
          </div>
        </div>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <div className="p-6">
      {/* Completion Modal - Case Data Checklist */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">✅</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Bank Statement Data Collected!</h2>
                  <p className="text-sm text-gray-600">
                    {jobStatus?.stats?.total_transactions || 0} transactions processed from your bank statements
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {/* Data Collected Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <span>📊</span> Data Collected from Financial Statements
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white rounded p-2">
                    <span className="text-gray-600">Transactions:</span>
                    <span className="ml-2 font-semibold text-green-700">{jobStatus?.stats?.total_transactions || 0}</span>
                  </div>
                  <div className="bg-white rounded p-2">
                    <span className="text-gray-600">Categories:</span>
                    <span className="ml-2 font-semibold text-green-700">Auto-classified</span>
                  </div>
                </div>
                <p className="text-xs text-green-700 mt-3">
                  💡 Supports: Bank statements, Credit card statements, QuickBooks exports
                </p>
              </div>

              {/* What's Still Needed */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                  <span>📋</span> Additional Data (Optional - Improves Accuracy)
                </h3>
                <p className="text-sm text-amber-800 mb-4">
                  Your transaction data will be used to reconstruct a P&L. For higher accuracy, consider adding:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-sm">
                    <span className="text-amber-600 mt-0.5">⬜</span>
                    <div>
                      <span className="font-medium text-amber-900">Credit Card Statements</span>
                      <p className="text-amber-700 text-xs mt-0.5">Capture detailed expenses not in bank statements</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <span className="text-amber-600 mt-0.5">⬜</span>
                    <div>
                      <span className="font-medium text-amber-900">Historical P&L or Tax Returns</span>
                      <p className="text-amber-700 text-xs mt-0.5">For baseline comparison (can be estimated without)</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <span className="text-green-600 mt-0.5">✅</span>
                    <div>
                      <span className="font-medium text-amber-900">Event Information</span>
                      <p className="text-amber-700 text-xs mt-0.5">Type of disruption, start/end dates (required)</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-sm">
                    <span className="text-green-600 mt-0.5">✅</span>
                    <div>
                      <span className="font-medium text-amber-900">Business Details</span>
                      <p className="text-amber-700 text-xs mt-0.5">Business type, location (required)</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Name Prompt Dialog */}
              {showNamePrompt && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name this job for easy identification:
                  </label>
                  <input
                    type="text"
                    value={businessNameInput}
                    onChange={(e) => setBusinessNameInput(e.target.value)}
                    placeholder="e.g., ABC Company Q1 2024"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleSaveForLater(businessNameInput)}
                      disabled={saving}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setShowNamePrompt(false)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleCreateReport}
                  className="w-full px-4 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <span>📝</span>
                  Continue Building Case
                  <span className="text-xs bg-blue-500 px-2 py-0.5 rounded">Add More Data</span>
                </button>
                
                <p className="text-xs text-gray-500 text-center">
                  You'll be guided through collecting the remaining information in the Case Builder wizard.
                </p>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleExportCSV}
                    disabled={exporting}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    {exporting ? 'Exporting...' : '📥 Export CSV'}
                  </button>
                  <button
                    onClick={() => handleSaveForLater()}
                    disabled={saving}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    {saving ? 'Saving...' : 'Save & Continue Later'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Mobile Header */}
          <div className="md:hidden">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-xl font-bold text-gray-900">Transaction Validation</h1>
              <button
                onClick={() => navigate('/cleanup')}
                className="p-2 text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex items-center gap-2 text-sm mb-3">
              <p className="text-gray-500">Job: {jobId?.slice(0, 8)}...</p>
              {jobStatus?.report_id && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                  📋 {jobStatus.report_name || 'Report'}
                </span>
              )}
            </div>
            
            {/* Mobile Action Buttons */}
            <div className="flex gap-2">
              {jobStatus && (
                <button
                  onClick={handleMarkComplete}
                  className={`flex-1 py-3 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 ${
                    jobStatus.stats.needs_review === 0
                      ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg'
                      : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                  }`}
                >
                  {jobStatus.stats.needs_review === 0 ? (
                    <>✅ Complete</>
                  ) : (
                    <>⚡ Complete ({jobStatus.stats.needs_review})</>
                  )}
                </button>
              )}
              <button
                onClick={handleExportCSV}
                disabled={exporting}
                className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg border border-gray-300"
              >
                📥
              </button>
            </div>
          </div>
          
          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Transaction Validation</h1>
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-500">Job: {jobId?.slice(0, 8)}...</p>
                {jobStatus?.report_id && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                    📋 Linked to: {jobStatus.report_name || 'Report'}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Progress Indicator */}
              {jobStatus && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">Progress:</span>
                  <span className={`font-bold ${jobStatus.progress_pct >= 100 ? 'text-green-600' : 'text-blue-600'}`}>
                    {jobStatus.progress_pct}%
                  </span>
                  <span className="text-gray-400">
                    ({(jobStatus.stats.reviewed || 0) + (jobStatus.stats.auto_approved || 0)} / {jobStatus.stats.total_transactions})
                  </span>
                </div>
              )}
              
              {/* Export Button */}
              <button
                onClick={handleExportCSV}
                disabled={exporting}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 text-gray-700 font-semibold rounded-lg transition-colors flex items-center gap-2 border border-gray-300"
              >
                {exporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    Exporting...
                  </>
                ) : (
                  <>📥 Export CSV</>
                )}
              </button>
              
              {/* PRIMARY ACTION: Complete & Return to Report Button - ALWAYS VISIBLE */}
              {jobStatus && (
                <button
                  onClick={handleMarkComplete}
                  className={`px-5 py-2 font-semibold rounded-lg transition-colors flex items-center gap-2 ${
                    jobStatus.stats.needs_review === 0
                      ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg'
                      : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                  }`}
                  title={
                    jobStatus.stats.needs_review === 0
                      ? 'Complete cleanup and continue to report'
                      : `${jobStatus.stats.needs_review} items still need review - click to complete anyway`
                  }
                >
                  {jobStatus.stats.needs_review === 0 ? (
                    <>✅ Complete & Return to Report</>
                  ) : (
                    <>⚡ Complete ({jobStatus.stats.needs_review} pending)</>
                  )}
                </button>
              )}
              
              <button
                onClick={() => navigate('/cleanup')}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg"
              >
                ← Back to Jobs
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Bar */}
      {jobStatus && (
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{jobStatus.stats.total_transactions}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{jobStatus.stats.auto_approved}</div>
                <div className="text-xs text-gray-500">Auto-Approved</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{jobStatus.stats.needs_review}</div>
                <div className="text-xs text-gray-500">Needs Review</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{jobStatus.stats.reviewed || 0}</div>
                <div className="text-xs text-gray-500">Reviewed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{jobStatus.progress_pct}%</div>
                <div className="text-xs text-gray-500">Complete</div>
              </div>
              <div className="text-center">
                <span className={`px-3 py-1 rounded-full text-sm ${
                  jobStatus.status === 'completed' ? 'bg-green-100 text-green-800' :
                  jobStatus.status === 'pending_review' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {jobStatus.status.replace('_', ' ')}
                </span>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${jobStatus.progress_pct}%` }}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Search & Filter Bar */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search descriptions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            
            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            
            {/* Toggle Filters */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-2 border rounded-lg flex items-center gap-2 ${
                showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-600'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
            </button>
            
            {/* Bulk Actions */}
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 ml-2 pl-2 border-l">
                <span className="text-sm text-gray-600">{selectedIds.size} selected</span>
                <button
                  onClick={() => {
                    // Bulk approve logic would go here
                    console.log(`Bulk approve ${selectedIds.size} transactions`);
                  }}
                  className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200"
                >
                  ✓ Approve All
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="px-2 py-1 text-gray-500 hover:text-gray-700 text-sm"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          
          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Amount:</label>
                <input
                  type="number"
                  placeholder="Min"
                  value={amountMin}
                  onChange={(e) => setAmountMin(e.target.value)}
                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={amountMax}
                  onChange={(e) => setAmountMax(e.target.value)}
                  className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </div>
              
              <button
                onClick={() => {
                  setSearchQuery('');
                  setCategoryFilter('');
                  setAmountMin('');
                  setAmountMax('');
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Anomaly Banner - Always show */}
      <div className={`border-b ${unresolvedAnomalies.length > 0 ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200' : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'}`}>
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{unresolvedAnomalies.length > 0 ? '⚠️' : '✅'}</span>
              <div>
                {unresolvedAnomalies.length > 0 ? (
                  <>
                    <span className="font-semibold text-amber-800">
                      {unresolvedAnomalies.length} Anomal{unresolvedAnomalies.length === 1 ? 'y' : 'ies'} Detected
                    </span>
                    <span className="text-amber-700 text-sm ml-2">
                      ({unresolvedAnomalies.filter(a => a.severity === 'critical' || a.severity === 'high').length} high priority)
                    </span>
                  </>
                ) : (
                  <span className="font-semibold text-green-800">
                    No Anomalies Detected
                  </span>
                )}
              </div>
            </div>
            {unresolvedAnomalies.length > 0 && (
              <button
                onClick={() => setShowAnomalies(!showAnomalies)}
                className="px-3 py-1 text-sm bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors"
              >
                {showAnomalies ? 'Hide Details' : 'View Details'}
              </button>
            )}
          </div>
          
          {/* Expanded Anomaly List */}
          {showAnomalies && unresolvedAnomalies.length > 0 && (
            <div className="mt-4 space-y-3">
              {unresolvedAnomalies.slice(0, showAllAnomalies ? unresolvedAnomalies.length : 10).map((anomaly, idx) => (
                <div 
                  key={anomaly.id || idx} 
                  className={`p-3 rounded-lg ${
                    anomaly.severity === 'critical' ? 'bg-red-100 border border-red-300' :
                    anomaly.severity === 'high' ? 'bg-orange-100 border border-orange-300' :
                    anomaly.severity === 'medium' ? 'bg-yellow-100 border border-yellow-300' :
                    'bg-gray-100 border border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          anomaly.severity === 'critical' ? 'bg-red-200 text-red-800' :
                          anomaly.severity === 'high' ? 'bg-orange-200 text-orange-800' :
                          anomaly.severity === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {anomaly.severity?.toUpperCase()}
                        </span>
                        <span className="font-medium text-gray-900">{anomaly.title}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{anomaly.description}</p>
                      {anomaly.explanation && (
                        <p className="text-xs text-gray-500 mt-2 leading-relaxed">{anomaly.explanation}</p>
                      )}
                      {/* Show related transaction info if available */}
                      {anomaly.related_amount && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs text-gray-500">Amount:</span>
                          <span className="text-sm font-medium">${anomaly.related_amount?.toLocaleString()}</span>
                          {anomaly.transaction_id && (
                            <button
                              onClick={async () => {
                                // Set the highlight
                                setHighlightedTransactionId(anomaly.transaction_id);
                                setFilter('all');
                                
                                // Fetch the page containing the target transaction
                                try {
                                  const headers = await getAuthHeader();
                                  const response = await axios.get(
                                    `${BACKEND_URL}/api/v1/cleanup/jobs/${jobId}/transactions`,
                                    { 
                                      headers, 
                                      params: { 
                                        transaction_id: anomaly.transaction_id,
                                        page_size: 50 
                                      } 
                                    }
                                  );
                                  
                                  if (response.data?.transactions?.length > 0) {
                                    // Backend returns the page containing the target transaction
                                    setTransactions(response.data.transactions);
                                    // Update page to match the returned page from backend
                                    setPage(response.data.pagination?.page || 1);
                                    setTotalPages(response.data.pagination?.total_pages || 1);
                                    
                                    // Scroll to the transaction after render
                                    setTimeout(() => {
                                      const el = document.getElementById(`txn-${anomaly.transaction_id}`);
                                      if (el) {
                                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      } else {
                                        // Scroll to transactions table
                                        const tableEl = document.querySelector('[data-transactions-table]');
                                        if (tableEl) tableEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                      }
                                      
                                      // Auto-clear highlight after 5 seconds
                                      setTimeout(() => {
                                        setHighlightedTransactionId(null);
                                      }, 5000);
                                    }, 300);
                                  }
                                } catch (err) {
                                  console.error('Error fetching transaction:', err);
                                  // Fallback - scroll to transactions area
                                  const tableEl = document.querySelector('[data-transactions-table]');
                                  if (tableEl) tableEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 underline"
                            >
                              View Transaction
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {unresolvedAnomalies.length > 10 && (
                <button
                  onClick={() => setShowAllAnomalies(!showAllAnomalies)}
                  className="w-full py-2 text-sm text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors font-medium"
                >
                  {showAllAnomalies 
                    ? 'Show Less' 
                    : `Show ${unresolvedAnomalies.length - 10} More Anomalies`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Toolbar */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Filter */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="needs_review">Needs Review</option>
              <option value="all">All Transactions</option>
              <option value="approved">Approved</option>
            </select>
            
            {/* Bulk actions */}
            {selectedIds.size > 0 && (
              <button
                onClick={handleBulkApprove}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm cursor-pointer"
              >
                Approve Selected ({selectedIds.size})
              </button>
            )}
          </div>
          
          <div className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </div>
        </div>
      </div>
      
      {/* Error */}
      {error && (
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        </div>
      )}
      
      {/* Transaction Table */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {/* Desktop Table - Hidden on mobile */}
        <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden" data-transactions-table>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3 px-4 text-left">
                  <input 
                    type="checkbox"
                    checked={selectedIds.size === transactions.length && transactions.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody>
              {transactions
                .filter(txn => {
                  // Search filter
                  if (searchQuery && !txn.description.toLowerCase().includes(searchQuery.toLowerCase())) {
                    return false;
                  }
                  // Category filter
                  if (categoryFilter && txn.classification?.category !== categoryFilter) {
                    return false;
                  }
                  // Amount range filter
                  const amount = Math.abs(txn.amount);
                  if (amountMin && amount < parseFloat(amountMin)) {
                    return false;
                  }
                  if (amountMax && amount > parseFloat(amountMax)) {
                    return false;
                  }
                  return true;
                })
                .map(txn => (
                <TransactionRow
                  key={txn.id}
                  transaction={txn}
                  selected={selectedIds.has(txn.id)}
                  highlighted={highlightedTransactionId === txn.id}
                  onSelect={toggleSelect}
                  onApprove={handleApprove}
                  onReclassify={handleReclassify}
                />
              ))}
            </tbody>
          </table>
          
          {transactions.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No transactions found
            </div>
          )}
        </div>
        
        {/* Mobile Cards - Visible only on mobile */}
        <div className="md:hidden">
          {/* Mobile Header with Select All */}
          <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <input 
                type="checkbox"
                checked={selectedIds.size === transactions.length && transactions.length > 0}
                onChange={toggleSelectAll}
                className="rounded border-gray-300 w-5 h-5"
              />
              <span className="text-sm text-gray-600">Select All</span>
            </div>
            <span className="text-sm text-gray-500">
              {transactions.filter(txn => {
                if (searchQuery && !txn.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
                if (categoryFilter && txn.classification?.category !== categoryFilter) return false;
                const amount = Math.abs(txn.amount);
                if (amountMin && amount < parseFloat(amountMin)) return false;
                if (amountMax && amount > parseFloat(amountMax)) return false;
                return true;
              }).length} transactions
            </span>
          </div>
          
          {/* Mobile Swipe Instructions */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 px-4 py-2 flex items-center justify-center gap-4 text-xs">
            <span className="flex items-center gap-1 text-green-700">
              <span>👉</span> Swipe right to approve
            </span>
            <span className="text-gray-300">|</span>
            <span className="flex items-center gap-1 text-blue-700">
              Swipe left to edit <span>👈</span>
            </span>
          </div>
          
          {/* Transaction Cards */}
          <div className="bg-gray-50">
            {transactions
              .filter(txn => {
                if (searchQuery && !txn.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
                if (categoryFilter && txn.classification?.category !== categoryFilter) return false;
                const amount = Math.abs(txn.amount);
                if (amountMin && amount < parseFloat(amountMin)) return false;
                if (amountMax && amount > parseFloat(amountMax)) return false;
                return true;
              })
              .map(txn => (
                <TransactionCard
                  key={txn.id}
                  transaction={txn}
                  selected={selectedIds.has(txn.id)}
                  highlighted={highlightedTransactionId === txn.id}
                  onSelect={toggleSelect}
                  onApprove={handleApprove}
                  onReclassify={handleReclassify}
                />
              ))
            }
          </div>
          
          {transactions.length === 0 && (
            <div className="text-center py-12 text-gray-500 bg-white">
              No transactions found
            </div>
          )}
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded disabled:opacity-50 cursor-pointer"
            >
              Previous
            </button>
            <span className="px-3 py-1">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50 cursor-pointer"
            >
              Next
            </button>
          </div>
        )}
      </div>
      </div>
    </AppLayout>
  );
}
